import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {Decimal} from 'decimal.js';
import React, {useState} from 'react';

import {coreObjectArchiveAction} from 'app-core/platform/archivable/CoreObjectArchiveActions';
import {getUnarchiveAction} from 'archive/ArchiveActionsUtil';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import notifications from 'notifications';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {GENERIC_SHORT_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {isServerRequestError} from 'util/Net';

import {UnitTypesQuery} from './__generated/UnitTypesQuery';
import UnitArchiveWarningBanner from './UnitArchiveWarningBanner';
import {
  createUnit as createUnitHelper,
  CreateUnitParams,
  updateUnit as updateUnitHelper,
  UpdateUnitParams,
  updateUnitType,
} from './UnitDictionaryDAO';
import UnitTableRow from './UnitTableRow';
import UnitTypeEditModal, {isBase} from './UnitTypeEditModal';
import UnitTypeInventoryConfig from './UnitTypeInventoryConfig';
import UnitTypeTermsDisplay from './UnitTypeTermsDisplay';
import {isUnitDictionaryManagementEnabledForUser} from './util';

type UnitType = UnitTypesQuery['unitTypes'][number];
type Unit = UnitType['units'][number];

export interface UnitTypeSettingsTableProps {
  unitType: UnitType;
  allUnitTypes: UnitTypesQuery['unitTypes'];
  refetch: () => void;
  queryMatchedUnitsByName: Array<Unit>;
  queryMatchedUnitsBySymbol: Array<Unit>;
}

export default function UnitTypeSettingsTable({
  unitType,
  allUnitTypes,
  refetch,
  queryMatchedUnitsByName,
  queryMatchedUnitsBySymbol,
}: UnitTypeSettingsTableProps): JSX.Element {
  const isAdmin = isUnitDictionaryManagementEnabledForUser();
  const [isAddingNewUnit, setIsAddingNewUnit] = useState(false);
  const addUnitCreationRow = (): void => {
    setIsAddingNewUnit(true);
  };

  const addUnitButtonLabel = isAddingNewUnit ? 'Only one unit may be added at a time' : 'Add unit to class';

  const onArchiveUnitType = (): void => {
    const unitIds = unitType.units.filter((u) => !u.archiveData.archived).map((u) => u.id);

    ArchiveModal.open({
      items: [unitType],
      onArchive: (itemIds: Array<string>, purpose: string) => {
        coreObjectArchiveAction(itemIds, purpose);
        refetch();
      },
      renderArchivableItemTableOverride: () => (
        <UnitArchiveWarningBanner unitIds={unitIds} entityLabel="unitType" />
      ),
    });
  };

  const onUnarchiveUnitType = async (): Promise<void> => {
    const unarchiveFn = getUnarchiveAction([unitType.id]);
    await unarchiveFn([unitType.id]);
    refetch();
  };

  const tableHeader = (
    <div className="simpleDivTable-header u-position-relative">
      <div className="simpleDivTable-headerCell simpleDivTable-header--title simpleDivTable-cell--narrow">
        Units
        {isAdmin && (
          <Tooltip placement={FloatingPlacement.TOP} text={addUnitButtonLabel}>
            <BenchlingIcon
              aria-disabled={isAddingNewUnit}
              aria-label={addUnitButtonLabel}
              className={classNames('simpleDivTable-addButton', {
                'simpleDivTable-addButton--disabled': isAddingNewUnit,
              })}
              {...getActionButtonRoleProps({
                onPress: () => {
                  if (!isAddingNewUnit) {
                    addUnitCreationRow();
                  }
                },
              })}
              icon="compose"
              dataTestAttrs={{component: 'UnitTypeSettingsTable', element: 'addUnitButton'}}
            />
          </Tooltip>
        )}
      </div>
      <div className="simpleDivTable-headerCell simpleDivTable-cell--narrow">Symbol</div>
      <div className="simpleDivTable-headerCell">Conversion factor</div>
      {isAdmin && (
        <div className={classNames('simpleDivTable-actionGroupContainer', css(styles.unitTypeActionButton))}>
          {unitType.archiveData.archived ? (
            <Tooltip text="Unarchive" placement={FloatingPlacement.TOP}>
              <Button
                aria-label="Unarchive"
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                icon="unarchive"
                onClick={onUnarchiveUnitType}
                size={ButtonSize.SMALL}
                dataTestAttrs={{component: 'UnitTypeSettingsTable', element: 'unarchiveUnitTypeButton'}}
                className="u-small-spacing-right"
              />
            </Tooltip>
          ) : (
            <Tooltip text="Archive" placement={FloatingPlacement.TOP}>
              <Button
                aria-label="Archive"
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                icon="archive"
                onClick={onArchiveUnitType}
                size={ButtonSize.SMALL}
                dataTestAttrs={{component: 'UnitTypeSettingsTable', element: 'archiveUnitTypeButton'}}
                className="u-small-spacing-right"
              />
            </Tooltip>
          )}
          {!unitType.archiveData.archived && !isBase(unitType) && (
            <Tooltip text="Edit" placement={FloatingPlacement.TOP}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                icon="edit"
                dataTestAttrs={{component: 'UnitTypeSettingsTable', element: 'editUnitTypeButton'}}
                onClick={async () => {
                  const result = await legacyRunModal(UnitTypeEditModal, {
                    unitType,
                    allUnitTypes,
                  });
                  if (result != null) {
                    try {
                      await updateUnitType({
                        id: unitType.id,
                        name: result.name,
                        terms: result.terms,
                        originalFormula: result.originalFormula,
                      });
                      notifications.success('Successfully updated unit type');
                      refetch();
                    } catch (e) {
                      if (isServerRequestError(e)) {
                        notifications.error(notifications.getFriendlyError(e) ?? GENERIC_SHORT_ERROR_MESSAGE);
                      } else {
                        throw e;
                      }
                    }
                  }
                }}
                size={ButtonSize.SMALL}
                text={null}
              />
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );

  const units = unitType.units.slice(); // mutable copy

  units.sort((a: Unit, b: Unit) => {
    if (a.archiveData.archived !== b.archiveData.archived) {
      return a.archiveData.archived ? 1 : -1;
    }

    if (a.id === unitType.baseUnit?.id) {
      return -1;
    } else if (b.id === unitType.baseUnit?.id) {
      return 1;
    } else {
      return new Decimal(a.conversionFactor).comparedTo(new Decimal(b.conversionFactor));
    }
  });

  const createUnit = async (params: CreateUnitParams): Promise<void> => {
    try {
      await createUnitHelper(params);
      notifications.success(`Successfully created unit ${params.symbol}`);
      refetch();
      setIsAddingNewUnit(false);
    } catch (e) {
      if (e.message && e.message.startsWith('Unit symbol')) {
        notifications.warn(e.message);
      } else {
        throw e;
      }
    }
  };

  const updateUnit = async (params: UpdateUnitParams): Promise<void> => {
    try {
      await updateUnitHelper(params);
      notifications.success(`Successfully updated unit ${params.symbol}`);
      refetch();
    } catch (e) {
      if (e.message && e.message.startsWith('Unit symbol')) {
        notifications.warn(e.message);
      } else {
        throw e;
      }
    }
  };

  const tableItems = units.map((unit) => (
    <UnitTableRow
      unit={unit}
      baseUnit={unitType.baseUnit}
      key={unit.id}
      unitTypeId={unitType.id}
      refetch={refetch}
      onSave={updateUnit}
      highlightName={queryMatchedUnitsByName.includes(unit)}
      highlightSymbol={queryMatchedUnitsBySymbol.includes(unit)}
    />
  ));

  if (isAddingNewUnit) {
    tableItems.push(
      <UnitTableRow
        key="addUnit"
        baseUnit={unitType.baseUnit}
        onCancel={() => {
          setIsAddingNewUnit(false);
        }}
        onSave={createUnit}
        unitTypeId={unitType.id}
        refetch={refetch}
      />
    );
  }

  return (
    <div
      className={css(styles.tableContainer)}
      key={unitType.id}
      {...getTestProps({component: 'UnitTypeSettingsTable', element: 'root'})}
    >
      <div className="sampleSchemaSettings-containerTable-name u-flex-display u-vertical-flex">
        <div className="sampleSchemaSettings-containerTable-nameContainer">
          <div className="sampleSchemaSettings-containerTable-nameHeader">Unit type</div>
          <div
            className="u-mini-spacing-top u-mini-spacing-bottom"
            {...getTestProps({component: 'UnitTypeSettingsTable', element: 'unitTypeName'})}
          >
            {unitType.name} {unitType.archiveData.archived && `(${unitType.archiveData.purpose})`}
          </div>
        </div>
        {unitType.terms.length > 0 && (
          <div className="sampleSchemaSettings-containerTable-nameContainer">
            <div className="sampleSchemaSettings-containerTable-nameHeader">Formula</div>
            <UnitTypeTermsDisplay terms={unitType.terms} />
          </div>
        )}
        <UnitTypeInventoryConfig unitType={unitType} refetch={refetch} />
      </div>
      <div className={css(styles.divTableWide)}>
        {tableHeader}
        {(() => {
          if (unitType.units.length === 0) {
            return (
              <div className="simpleDivTable-row">
                <div className="simpleDivTable-cell simpleDivTable-cell--emptyMessage">
                  This unit type does not have any units
                </div>
              </div>
            );
          }
          return tableItems;
        })()}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  tableContainer: {
    marginBottom: 26,
    width: '100%',
    border: 1,
    borderStyle: 'solid',
    borderColor: GRAY_LIGHT,
    borderRadius: 4,
    display: 'flex',
  },
  divTableWide: {
    width: '100%',
    minWidth: 500,
    order: 1,
    borderLeft: `1px solid ${GRAY_LIGHT}`,
  },
  unitTypeActionButton: {
    marginRight: '20px',
    marginTop: '-6px',
  },
});
